var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { deepGet, sortMapKeys } from '../../util/MapUtils';
import { replaceStateNextCompat } from '../../util/history';
/**
 * Recursive helper function that compiles an array of all possible size combinations in order of default preference
 * @returns - does not return a value - adds values to external array productSizeCombos
 */
var createSizeCombos = function (
/**
 * array used to build current size combination
 */
sizeCombination, 
/**
 * array of size values corresponding to one size options set
 */
currentSizes, 
/**
 * array of arrays corresponding to remaining size option sets for the product
 */
additionalSizes, 
/**
 * array storing all size combinations in preference order
 */
results) {
    if (sizeCombination === void 0) { sizeCombination = []; }
    if (currentSizes === void 0) { currentSizes = []; }
    if (additionalSizes === void 0) { additionalSizes = []; }
    if (results === void 0) { results = []; }
    if (!currentSizes.length) {
        return;
    }
    for (var i = 0; i < currentSizes.length; i += 1) {
        sizeCombination.push(currentSizes[i]);
        createSizeCombos(sizeCombination, additionalSizes[0], additionalSizes.slice(1), results);
        if (!additionalSizes.length) {
            // @ts-ignore
            results.push(Array.from(sizeCombination));
        }
        sizeCombination.pop();
    }
    if (!additionalSizes.length) {
        return results;
    }
};
/**
 * Given a list of size groups and a demographic,
 * returns a map of each size group and its default size.
 */
var getDefaultSizes = function (
/**
 * Array of size groups for the product
 */
sizes, 
/**
 * Demographic information containing gender and age
 */
demographic) {
    return demographic
        ? sizes.reduce(function (acc, size) {
            var _a, _b;
            var defaultSize = ['MALE'].includes(demographic.gender) &&
                size.options.find(function (s) { return s.id === 'l'; })
                ? 'l'
                : (['FEMALE', 'UNISEX'].includes(demographic.gender) &&
                    ((_a = size.options.find(function (s) { return ['m', 'm/l'].includes(s.id); })) === null || _a === void 0 ? void 0 : _a.id)) ||
                    ((_b = size.options[0]) === null || _b === void 0 ? void 0 : _b.id);
            acc.set(size.id, defaultSize);
            return acc;
        }, new Map())
        : new Map();
};
/**
 * Given a list of size groups and the default value for each group,
 * generates list of size options in preferred order.
 */
var generateSizeSets = function (
/**
 * Array of size groups for the product
 */
sizes, 
/**
 * Map containing default size values for each size group
 */
defaults) {
    return sizes.map(function (sizeGroup) {
        var sizeGroupId = sizeGroup.id;
        var groupsSizes = sizeGroup.options.map(function (size) { return size.id; });
        var defaultSize = defaults.get(sizeGroupId);
        if (defaultSize) {
            var reorderedSizes_1 = [defaultSize];
            groupsSizes.forEach(function (size) {
                if (size !== defaultSize)
                    reorderedSizes_1.push(size);
            });
            groupsSizes = reorderedSizes_1;
        }
        return groupsSizes;
    });
};
/**
 * Helper to preselect a color and size option based on gender and variant availability.
 * @returns Ordered map containing default color and size(s) for the product
 */
export var getDefaults = function (_a) {
    // Implementation note:
    // The goal of this implementation is to make the flow linear. Aside from a small validation step,
    // any value set in `defaults` is treated as immutable to ensure a predictable flow with no side effects.
    var _b;
    var 
    /**
     * Array of colors for the product
     */
    _c = _a.colors, 
    /**
     * Array of colors for the product
     */
    colors = _c === void 0 ? [] : _c, 
    /**
     * Array of size sets for the product
     */
    _d = _a.sizes, 
    /**
     * Array of size sets for the product
     */
    sizes = _d === void 0 ? [] : _d, 
    /**
     * String representing a preselected color from URL params, if any
     */
    _e = _a.selectedColor, 
    /**
     * String representing a preselected color from URL params, if any
     */
    selectedColor = _e === void 0 ? undefined : _e, 
    /**
     * Array representing preselected sizes from URL params, if any
     */
    _f = _a.selectedSizes, 
    /**
     * Array representing preselected sizes from URL params, if any
     */
    selectedSizes = _f === void 0 ? [] : _f, 
    /**
     * Array of demographic objects with gender and age for the product
     */
    _g = _a.demographics, 
    /**
     * Array of demographic objects with gender and age for the product
     */
    demographics = _g === void 0 ? null : _g, 
    /**
     * Map of all variants for the product
     */
    _h = _a.variantsMap, 
    /**
     * Map of all variants for the product
     */
    variantsMap = _h === void 0 ? new Map() : _h;
    var KEY_ORDER = __spreadArray(['color'], sizes.map(function (s) { return s.id; }), true);
    var defaults = new Map();
    // 1. Determine preset defaults based on params
    if (colors.length && selectedColor) {
        defaults.set('color', selectedColor);
    }
    if (sizes.length && selectedSizes.length) {
        selectedSizes.forEach(function (size) {
            defaults.set(size.groupName, size.size);
        });
    }
    // 2. Validate any preset values; if they don't match- remove them.
    defaults.forEach(function (value, key, self) {
        if (key === 'color' && !colors.find(function (c) { return c.id === value; })) {
            self.delete(key);
        }
        else if (key.includes('size')) {
            var sizeGroup = sizes.find(function (s) { return s.id === key; });
            if (!sizeGroup || !sizeGroup.options.find(function (sg) { return sg.id === value; })) {
                self.delete(key);
            }
        }
    });
    // 3. Short-circuit if we've covered all defaults.
    if (defaults.size === (colors.length > 0 ? 1 : 0) + sizes.length) {
        if (!colors.length) {
            defaults.set('color', undefined);
        }
        return sortMapKeys(defaults, KEY_ORDER);
    }
    // 4. Inject any unspecified defaults based on demographics and availability
    var specifiedSizeIds = sizes
        .filter(function (s) { return defaults.has(s.id); })
        .map(function (s) { return s.id; });
    // If there are colors and we only need to specify a color
    if (colors.length &&
        !defaults.has('color') &&
        specifiedSizeIds.length === sizes.length) {
        // Find first available color option and preselect it
        var firstAvailableColor = colors.find(function (c) {
            var variant = deepGet(variantsMap, __spreadArray([c.id], sizes.map(function (s) { return defaults.get(s.id); }), true), {});
            return variant.available;
        });
        if (firstAvailableColor) {
            defaults.set('color', firstAvailableColor.id);
        }
        else {
            defaults.set('color', colors[0].id);
        }
    }
    // Need to specify color and/or size(s)
    else {
        if (!colors.length) {
            // NOTE: Setting `color = undefined` is a hack and we shouldn't need to do it.
            // To limit scope of this refactor, the existing logic is left in-place.
            defaults.set('color', undefined);
        }
        // Find preferred default size for each size group, based on demographic data
        var demographic = (demographics === null || demographics === void 0 ? void 0 : demographics.length) === 1 ? demographics[0] : undefined;
        var preferredDefaultSizes = getDefaultSizes(sizes, demographic);
        var sizeSets = generateSizeSets(sizes, preferredDefaultSizes);
        var productSizeCombinations = [];
        createSizeCombos([], 
        // @ts-ignore
        sizeSets[0], sizeSets.slice(1), productSizeCombinations);
        // Find first available color option and preselect it
        // Iterate through size combinations to find first one that is available
        var includedIndices_1 = specifiedSizeIds.map(function (id) { return ({
            position: sizes.findIndex(function (s) { return s.id === id; }),
            value: defaults.get(id)
        }); });
        var possibleSizes = productSizeCombinations.filter(function (combo) {
            return includedIndices_1.every(function (idx) { return combo[idx.position] === idx.value; });
        });
        var possibleColors = defaults.has('color')
            ? [{ id: defaults.get('color') }]
            : colors;
        // Iterate through the variant map to find the first
        // in stock color and size combination and return that as the default
        var foundAvailable = false;
        for (var x = 0; x < possibleColors.length; x += 1) {
            var _loop_1 = function (y) {
                var variant = deepGet(variantsMap, __spreadArray([(_b = possibleColors[x]) === null || _b === void 0 ? void 0 : _b.id], possibleSizes[y], true), false);
                var isAvailable = variant === null || variant === void 0 ? void 0 : variant.available;
                if (isAvailable) {
                    if (!defaults.has('color')) {
                        defaults.set('color', possibleColors[x].id);
                    }
                    sizes.forEach(function (group) {
                        if (!specifiedSizeIds.includes(group.id)) {
                            defaults.set(group.id, variant.sizes.find(function (s) { return s.title === group.id; }).id);
                        }
                    });
                    foundAvailable = true;
                    return "break";
                }
            };
            for (var y = 0; y < possibleSizes.length; y += 1) {
                var state_1 = _loop_1(y);
                if (state_1 === "break")
                    break;
            }
            // This is only necessary due to the `color = undefined` condition above.
            if (defaults.size > 1)
                break;
        }
        // No available variants were found
        if (!foundAvailable) {
            if (colors.length && !defaults.has('color')) {
                defaults.set('color', colors[0].id);
            }
            if (preferredDefaultSizes.size) {
                preferredDefaultSizes.forEach(function (val, key) { return defaults.set(key, val); });
            }
        }
    }
    return sortMapKeys(defaults, KEY_ORDER);
};
/**
 * updates the product url params based on the selected options without reloading the page
 */
export var updateURL = function (
/**
 * a map of color and size options
 */
options) {
    // Early return if not in browser environment to prevent hydration issues
    if (typeof window === 'undefined') {
        return;
    }
    var color = options.get('color');
    var sizes = [];
    options.forEach(function (value, key) {
        if (key.includes('size')) {
            sizes.push([key, value]);
        }
    });
    var nextUrl = new URL(window.location.href);
    if (color)
        nextUrl.searchParams.set('variant', color);
    sizes.forEach(function (_a) {
        var sizeType = _a[0], value = _a[1];
        if (sizeType && value) {
            nextUrl.searchParams.set(sizeType, value);
        }
    });
    replaceStateNextCompat(null, '', nextUrl);
};
/**
 * Maps product categories to standardized category constants
 */
var CATEGORY_KEYWORD_MAP = {
    socks: 'socks',
    slipper: 'slippers',
    underwear: 'underwear',
    bralettes: 'bralettes',
    shirt: 't-shirts'
};
/**
 * Determines the category constant from a list of product categories
 */
var assignCategoryConstant = function (
/**
 * Array of product category strings
 */
categories) {
    var keywords = Object.keys(CATEGORY_KEYWORD_MAP);
    // Find the first category that contains any of our keywords
    var matchingCategory = categories.find(function (category) {
        var lowerCaseCategory = category.toLowerCase();
        return keywords.some(function (keyword) { return lowerCaseCategory.includes(keyword); });
    });
    if (!matchingCategory) {
        return null;
    }
    // Find which keyword matched and return its mapped value
    var lowerCaseCategory = matchingCategory.toLowerCase();
    var foundKeyword = keywords.find(function (keyword) {
        return lowerCaseCategory.includes(keyword);
    });
    return foundKeyword
        ? CATEGORY_KEYWORD_MAP[foundKeyword]
        : null;
};
/**
 * Evaluates whether a given product attribute rule matches the provided product attribute values.
 */
var checkRuleMatch = function (
/**
 * The ProductAttributeRule to evaluate
 */
rule, 
/**
 * The product attribute values to check against the rule.
 * This can be an array of strings, a boolean, null, or undefined.
 */
productAttributeValues) {
    if (productAttributeValues == null || !rule.key) {
        return {};
    }
    var exclude = rule.excluded || false;
    var values = rule.values || [];
    // Handle array values
    if (Array.isArray(productAttributeValues)) {
        var hasMatch = values.some(function (value) {
            return productAttributeValues
                .map(function (v) { return v.toLowerCase(); })
                .includes(value.toLowerCase());
        });
        if (hasMatch) {
            return exclude ? { disqualified: true } : { shouldInclude: true };
        }
    }
    // Handle boolean values
    if (typeof productAttributeValues === 'boolean') {
        var hasMatch = values.some(function (value) { return value.toLowerCase() === productAttributeValues.toString(); });
        if (hasMatch) {
            return exclude ? { disqualified: true } : { shouldInclude: true };
        }
    }
    return {};
};
/**
 * Helper function to get the value for a rule key from either productAttributes or productData
 * This allows rules to reference both attributes (product.attributes.key) and top-level product fields
 *
 * Supported top-level fields:
 * - gender: Product gender from demographics array (e.g., 'MALE', 'FEMALE', 'UNISEX')
 * - age: Product age from demographics array (e.g., 'ADULT', 'KIDS')
 * - collections: Product collections array
 * - categories: Product categories array
 * - Other fields: Automatically normalized to consistent format
 */
var getValueForRuleKey = function (
/**
 * The rule key to look up
 */
key, 
/**
 * Product attributes object
 */
attributes, 
/**
 * Full product data object
 */
data) {
    // First check if it's in productAttributes
    if (attributes && key in attributes) {
        return attributes[key];
    }
    // Handle demographic fields (gender and age)
    if (data && (key === 'gender' || key === 'age')) {
        var demographics = data.demographics;
        if (demographics && Array.isArray(demographics)) {
            var values = demographics
                .map(function (demo) { return demo[key]; })
                .filter(function (value) { return value !== undefined && value !== null; });
            if (key === 'gender' && values.length > 0) {
                if (values.length > 0) {
                    return Array.from(new Set(values));
                }
            }
            return values.length > 0 ? Array.from(new Set(values)) : undefined;
        }
    }
    // Then check if it's a top-level product field
    if (data && key in data) {
        var value = data[key];
        // Handle common product-level fields
        switch (key) {
            case 'collections':
            case 'categories':
                return Array.isArray(value)
                    ? value
                    : typeof value === 'string'
                        ? [value]
                        : value;
            default:
                // For other fields, try to normalize to array format for consistent handling
                if (Array.isArray(value)) {
                    return value;
                }
                if (typeof value === 'string') {
                    return [value];
                }
                if (typeof value === 'boolean') {
                    return value;
                }
                return value;
        }
    }
    return undefined;
};
/**
 * Retrieves a list of product callouts based on the provided product attributes,
 * attribute settings, and category.
 *
 * @returns {Omit<Attribute, 'rules'>[]} An array of attributes excluding their rules, representing the product callouts.
 *
 * @remarks
 * - The `firstMatchOnly` flag in a callout stops further processing of attributes within that product attribute callout once a match is found.
 */
export var getProductCallouts = function (_a) {
    var 
    /**
     * The attributes of the product
     */
    productAttributes = _a.productAttributes, 
    /**
     * The full product data (for non-attribute fields like gender, age, collections)
     */
    productData = _a.productData, 
    /**
     * The settings that define attribute callouts
     */
    attributeSettings = _a.attributeSettings, 
    /**
     * The category of the product
     */
    category = _a.category;
    if (!attributeSettings || !category)
        return [];
    // Find the resource that matches the category
    var categorySettings = attributeSettings === null || attributeSettings === void 0 ? void 0 : attributeSettings.find(function (resource) { var _a; return ((_a = resource.category) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === category.toLowerCase(); });
    if (!(categorySettings === null || categorySettings === void 0 ? void 0 : categorySettings.productAttributeCallouts))
        return [];
    var results = [];
    categorySettings.productAttributeCallouts.forEach(function (callout) {
        var firstMatchOnly = callout.firstMatchOnly;
        (callout.attributes || []).some(function (attribute) {
            var referenceAttributes = attribute.referenceAttributes, remainingAttributeData = __rest(attribute, ["referenceAttributes"]);
            var rulesToCheck = referenceAttributes || [];
            var shouldInclude = false;
            var disqualified = false;
            // Special handling for demographic-based rules (gender, age)
            var demographicKeys = ['gender', 'age'];
            var hasDemographicRules = rulesToCheck.some(function (rule) {
                return demographicKeys.includes(rule.key);
            });
            if (hasDemographicRules && (productData === null || productData === void 0 ? void 0 : productData.demographics)) {
                // For demographic rules, check each demographic object individually
                var demographics = productData.demographics;
                demographics.some(function (demographic) {
                    var demographicMatches = true;
                    var demographicDisqualified = false;
                    // Check all rules against this specific demographic object
                    rulesToCheck.some(function (rule) {
                        if (!rule.key)
                            return false;
                        var productAttributeValues;
                        // Get values from the specific demographic object or other sources
                        if (demographicKeys.includes(rule.key)) {
                            var value = demographic[rule.key];
                            productAttributeValues = value ? [value] : undefined;
                        }
                        else {
                            productAttributeValues = getValueForRuleKey(rule.key === 'collection' ? 'collections' : rule.key, productAttributes, productData);
                        }
                        var _a = checkRuleMatch(rule, productAttributeValues), match = _a.shouldInclude, exclude = _a.disqualified;
                        if (exclude) {
                            demographicDisqualified = true;
                            return true;
                        }
                        if (!match) {
                            demographicMatches = false;
                        }
                        return false;
                    });
                    // If this demographic object matches all rules and isn't disqualified
                    if (demographicMatches && !demographicDisqualified) {
                        shouldInclude = true;
                        return true; // Stop checking other demographics
                    }
                    if (demographicDisqualified) {
                        disqualified = true;
                        return true; // Stop checking other demographics
                    }
                    return false;
                });
            }
            else {
                // Original logic for non-demographic rules
                rulesToCheck.some(function (rule) {
                    if (!rule.key)
                        return false;
                    // Use helper function to get values from either productAttributes or productData
                    var productAttributeValues = getValueForRuleKey(rule.key === 'collection' ? 'collections' : rule.key, productAttributes, productData);
                    var _a = checkRuleMatch(rule, productAttributeValues), match = _a.shouldInclude, exclude = _a.disqualified;
                    if (exclude) {
                        disqualified = true;
                        return true;
                    }
                    if (match) {
                        shouldInclude = true;
                    }
                    return false;
                });
            }
            if (shouldInclude && !disqualified) {
                results.push(remainingAttributeData);
                // Stop checking attributes in this callout if firstMatchOnly is true
                return firstMatchOnly;
            }
            return false;
        });
    });
    return results;
};
/**
 * Enhanced version of getProductCallouts that can work with raw product data
 * @returns Array of callouts for the product
 */
export var getProductCalloutsForRawData = function (
/**
 * Product data with attributes and categories
 */
productData, 
/**
 * Configuration for product cards attributes and callouts
 */
attributeSettings) {
    var _a;
    if (!(attributeSettings === null || attributeSettings === void 0 ? void 0 : attributeSettings.length) || !((_a = productData === null || productData === void 0 ? void 0 : productData.categories) === null || _a === void 0 ? void 0 : _a.length)) {
        return [];
    }
    var category = assignCategoryConstant(productData.categories);
    if (!category) {
        return [];
    }
    return getProductCallouts({
        productAttributes: productData.attributes,
        productData: productData,
        attributeSettings: attributeSettings,
        category: category
    });
};
/**
 * Universal product enhancement function that adds callouts to any product-like object
 * @returns Enhanced product item with callouts
 */
export var enhanceAnyProductWithCallouts = function (
/**
 * Product item or raw product data
 */
productItem, 
/**
 * Configuration for product cards attributes and callouts
 */
productCardsConfig) {
    if (!productCardsConfig) {
        return productItem;
    }
    // Extract product data from different item structures
    var productData;
    var isNestedProduct = false;
    if (productItem.__typename === 'ProductCard' && 'product' in productItem) {
        productData = productItem.product;
        isNestedProduct = true;
    }
    else if (productItem.__typename === 'Product' ||
        'categories' in productItem ||
        ('attributes' in productItem && 'categories' in productItem)) {
        productData = productItem;
    }
    // If no valid product data found, return unchanged
    if (!productData) {
        return productItem;
    }
    // Generate callouts for the product data
    var callouts = getProductCalloutsForRawData(productData, productCardsConfig);
    // Return enhanced item with callouts in the appropriate structure
    if (isNestedProduct) {
        return __assign(__assign({}, productItem), { product: __assign(__assign({}, productData), { callouts: callouts }) });
    }
    return __assign(__assign({}, productItem), { callouts: callouts });
};
export default {};
